import type { AvailableModel, UsageModelRow } from '../stores/usage'

// Formatting helpers shared between UsageView's weekly-spend section and
// its available-models table (#1090) — extracted so neither component
// duplicates cents/token formatting.

const TOKEN_UNIT_MILLION = 1_000_000
const TOKEN_UNIT_THOUSAND = 1_000

// Token sum across the four buckets. Typed on the shared token fields
// (not the full row) so it serves both model rows and operation rows.
type TokenCounts = Pick<UsageModelRow, 'input_tokens' | 'output_tokens' | 'cache_read_tokens' | 'cache_creation_tokens'>

// Format cents as $X.XX. Below $0.01 collapses to "<$0.01" so a week
// with $0.003 doesn't read as "$0.00 spent" (misleading when the user
// is checking whether prompt-caching savings actually landed).
export function formatUsd(cents: number): string {
  if (cents === 0) return '$0.00'
  if (cents < 1) return '<$0.01'
  return `$${(cents / 100).toFixed(2)}`
}

export function formatTokens(tokens: number): string {
  if (tokens >= TOKEN_UNIT_MILLION) return `${(tokens / TOKEN_UNIT_MILLION).toFixed(1)}M`
  if (tokens >= TOKEN_UNIT_THOUSAND) return `${(tokens / TOKEN_UNIT_THOUSAND).toFixed(1)}k`
  return tokens.toString()
}

export function totalTokens(row: TokenCounts): number {
  return row.input_tokens + row.output_tokens + row.cache_read_tokens + row.cache_creation_tokens
}

// Prompt-cache hit rate: the share of input that was served from
// cache rather than re-read fresh. `cache_read_tokens` are cache hits,
// `input_tokens` the non-cached input, so the denominator is their sum
// (cache_creation is the one-time write, excluded). Null when there was
// no input at all — nothing to express a rate over.
export function cacheHitRate(row: UsageModelRow): number | null {
  const considered = row.cache_read_tokens + row.input_tokens
  if (considered === 0) return null
  return Math.round((row.cache_read_tokens / considered) * 100)
}

// Stable color per model family. Keeps the bar legend visually
// consistent across weeks — if a model ever moves between Sonnet /
// Haiku / Opus, the color hint moves with it.
export function modelColor(model: string): string {
  if (model.includes('opus')) return 'bg-purple-500'
  if (model.includes('sonnet')) return 'bg-blue-500'
  if (model.includes('haiku')) return 'bg-emerald-500'
  return 'bg-zinc-500'
}

export function shortModel(model: string): string {
  return model.replace(/^claude-/, '').replace(/-\d{8}$/, '')
}

// Context window as a compact "1M" / "200K". Null (offline static fallback,
// or a model the API didn't size) renders as a dash.
export function formatContext(tokens: number | null): string {
  if (tokens === null) return '—'
  if (tokens >= TOKEN_UNIT_MILLION) return `${tokens / TOKEN_UNIT_MILLION}M`
  if (tokens >= TOKEN_UNIT_THOUSAND) return `${Math.round(tokens / TOKEN_UNIT_THOUSAND)}K`
  return tokens.toString()
}

// Prices are US cents per 1M tokens ($5.00 / $0.50). Unlike spend, a rate
// is exact, so fractional cents keep their third decimal ($0.125) instead of
// rounding. Null pricing (a model we don't have a maintained price for)
// shows a dash.
export function formatRate(cents: number | undefined): string {
  if (cents === undefined) return '—'
  const decimals = Number.isInteger(cents) ? 2 : 3
  return `$${(cents / 100).toFixed(decimals)}`
}

export function modelName(model: AvailableModel): string {
  return model.display_name ?? shortModel(model.id)
}
